import { z } from 'zod'
import { contentVisibilitySchema } from './contentVisibility'

export const inventoryFamilySchema = z.enum(['asset', 'license', 'consumable'])
export const inventoryEntityTypeSchema = z.enum([
  'asset',
  'resource',
  'consumable_batch',
  'kit',
  'license_pool',
  'category',
  'model',
])
export const inventoryTargetSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('user'), id: z.string().uuid() }).strict(),
  z.object({ type: z.literal('resource'), id: z.string().uuid() }).strict(),
  z.object({ type: z.literal('asset'), id: z.string().uuid() }).strict(),
])

const nullableId = z.string().uuid().nullable().optional()
const shortText = z.string().trim().min(1).max(160)
const notes = z.string().trim().max(10_000).nullable().optional()
export const moneySchema = z
  .object({
    amountCents: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
    currency: z.string().regex(/^[A-Z]{3}$/),
  })
  .strict()
export const inventoryPaginationSchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(160).default(''),
    sort: z.enum(['createdAt', 'updatedAt', 'name', 'assetTag']).default('updatedAt'),
    direction: z.enum(['asc', 'desc']).default('desc'),
    archived: z.enum(['exclude', 'include', 'only']).default('exclude'),
  })
  .strict()

export const customFieldTypeSchema = z.enum([
  'text',
  'textarea',
  'integer',
  'decimal',
  'boolean',
  'date',
  'datetime',
  'url',
  'email',
  'select',
  'multiselect',
])
export const customFieldInputSchema = z
  .object({
    name: shortText,
    key: z
      .string()
      .trim()
      .regex(/^[a-z][a-z0-9_]{1,63}$/),
    description: z.string().trim().max(1000).nullable().optional(),
    type: customFieldTypeSchema,
    position: z.number().int().min(0).max(10_000).default(0),
    required: z.boolean().default(false),
    defaultValue: z.unknown().optional(),
    options: z.array(z.string().trim().min(1).max(200)).max(500).default([]),
    min: z.number().finite().nullable().optional(),
    max: z.number().finite().nullable().optional(),
    maxLength: z.number().int().min(1).max(100_000).nullable().optional(),
    regex: z.string().max(500).nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.regex) {
      try {
        void new RegExp(value.regex, 'u')
      } catch {
        ctx.addIssue({ code: 'custom', path: ['regex'], message: 'Expression régulière invalide' })
      }
    }
    if (value.min != null && value.max != null && value.min > value.max)
      ctx.addIssue({
        code: 'custom',
        path: ['min'],
        message: 'Le minimum doit être inférieur au maximum',
      })
    if (['select', 'multiselect'].includes(value.type) && value.options.length === 0)
      ctx.addIssue({
        code: 'custom',
        path: ['options'],
        message: 'Au moins une option est requise',
      })
  })

export type CustomFieldDefinition = z.infer<typeof customFieldInputSchema> & { id: string }
export const customFieldBindingsSchema = z
  .array(
    z
      .object({
        targetType: z.enum(['category', 'model', 'resource_type']),
        targetId: z.string().uuid(),
      })
      .strict(),
  )
  .max(500)
  .refine(
    (items) =>
      new Set(items.map((item) => `${item.targetType}:${item.targetId}`)).size === items.length,
    'Affectation dupliquée',
  )
export const customFieldWriteSchema = z
  .object({ field: customFieldInputSchema, bindings: customFieldBindingsSchema })
  .strict()
export const customFieldLifecycleSchema = z
  .object({ action: z.enum(['archive', 'restore', 'purge']) })
  .strict()
export function validateCustomFieldValue(field: CustomFieldDefinition, input: unknown) {
  if ((input === null || input === undefined || input === '') && !field.required) return null
  let schema: z.ZodType
  if (field.type === 'boolean') schema = z.boolean()
  else if (field.type === 'integer') schema = z.number().int()
  else if (field.type === 'decimal') schema = z.number().finite()
  else if (field.type === 'date' || field.type === 'datetime')
    schema = z.iso.datetime({ offset: true }).or(z.iso.date())
  else if (field.type === 'url') schema = z.url().max(field.maxLength ?? 2048)
  else if (field.type === 'email') schema = z.email().max(field.maxLength ?? 320)
  else if (field.type === 'multiselect')
    schema = z
      .array(z.string())
      .max(field.options.length)
      .refine((values) => values.every((value) => field.options.includes(value)), 'Option inconnue')
  else schema = z.string().max(field.maxLength ?? (field.type === 'textarea' ? 100_000 : 10_000))
  const parsed = schema.parse(input)
  if (
    typeof parsed === 'number' &&
    ((field.min != null && parsed < field.min) || (field.max != null && parsed > field.max))
  )
    throw new Error('Valeur hors limites')
  if (typeof parsed === 'string' && field.type === 'select' && !field.options.includes(parsed))
    throw new Error('Option inconnue')
  if (typeof parsed === 'string' && field.regex && !new RegExp(field.regex, 'u').test(parsed))
    throw new Error('Format invalide')
  return parsed
}

export const inventoryStatusInputSchema = z
  .object({
    name: shortText,
    key: z
      .string()
      .trim()
      .regex(/^[a-z][a-z0-9_-]{1,63}$/),
    tone: z.enum(['neutral', 'blue', 'green', 'orange', 'red']),
    icon: z.string().trim().max(64).nullable().optional(),
    position: z.number().int().min(0).max(10_000).default(0),
    families: z.array(inventoryFamilySchema).min(1).max(3),
    available: z.boolean(),
    checkoutAllowed: z.boolean(),
    checkinAllowed: z.boolean(),
    targetRequired: z.boolean(),
    commentRequired: z.boolean(),
    terminal: z.boolean(),
    countsAsAvailable: z.boolean(),
    config: z.record(z.string(), z.unknown()).default({}),
  })
  .strict()
export const inventoryResourceTypeInputSchema = z
  .object({
    name: shortText,
    key: z
      .string()
      .trim()
      .regex(/^[a-z][a-z0-9_-]{1,63}$/),
    icon: z.string().max(64).nullable().optional(),
    position: z.number().int().min(0).max(10_000).default(0),
    visibility: contentVisibilitySchema.optional(),
  })
  .strict()
export const inventoryResourceInputSchema = z
  .object({
    typeId: z.string().uuid(),
    parentId: nullableId,
    name: shortText,
    description: z.string().trim().max(2000).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).default({}),
    customValues: z.record(z.string(), z.unknown()).default({}),
    visibility: contentVisibilitySchema.optional(),
  })
  .strict()
export const inventoryCatalogEntityInputSchema = z
  .object({
    name: shortText,
    description: z.string().trim().max(2000).nullable().optional(),
    website: z.url().max(2048).nullable().optional(),
    visibility: contentVisibilitySchema.optional(),
  })
  .strict()
export const inventoryCategoryInputSchema = z
  .object({
    family: inventoryFamilySchema,
    name: shortText,
    description: z.string().trim().max(2000).nullable().optional(),
    invoicePolicy: z.enum(['disabled', 'optional', 'required']).nullable().optional(),
    visibility: contentVisibilitySchema.optional(),
  })
  .strict()
export const inventorySupplierInputSchema = inventoryCatalogEntityInputSchema
  .extend({
    email: z.email().max(320).nullable().optional(),
    phone: z.string().trim().max(80).nullable().optional(),
  })
  .strict()
export const inventoryModelInputSchema = z
  .object({
    family: inventoryFamilySchema,
    categoryId: z.string().uuid(),
    manufacturerId: nullableId,
    name: shortText,
    modelNumber: z.string().trim().max(120).nullable().optional(),
    notes,
    autoCreateConsumable: z.boolean().default(false),
    consumableUnit: z.string().trim().min(1).max(40).default('unité'),
    consumablePrecision: z.number().int().min(0).max(6).default(0),
    visibility: contentVisibilitySchema.optional(),
    customValues: z.record(z.string(), z.unknown()).default({}),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.autoCreateConsumable && value.family !== 'consumable')
      context.addIssue({
        code: 'custom',
        path: ['autoCreateConsumable'],
        message: 'Disponible uniquement pour les modèles consommables',
      })
  })
export const inventoryAssetInputSchema = z
  .object({
    modelId: z.string().uuid(),
    statusId: z.string().uuid(),
    resourceId: nullableId,
    serialNumber: z.string().trim().max(255).nullable().optional(),
    name: z.string().trim().max(160).nullable().optional(),
    notes,
    purchaseId: nullableId,
    warrantyEndsAt: z.iso.datetime({ offset: true }).nullable().optional(),
    depreciatesAt: z.iso.datetime({ offset: true }).nullable().optional(),
    customValues: z.record(z.string(), z.unknown()).default({}),
    visibility: contentVisibilitySchema.optional(),
  })
  .strict()
export const inventoryCheckoutSchema = z
  .object({
    target: inventoryTargetSchema.refine((value) => value.type !== 'asset', 'Cible invalide'),
    statusId: z.string().uuid(),
    comment: z.string().trim().max(4000).nullable().optional(),
  })
  .strict()
export const inventoryCheckinSchema = z
  .object({
    resourceId: nullableId,
    statusId: z.string().uuid(),
    comment: z.string().trim().max(4000).nullable().optional(),
  })
  .strict()
export const inventoryConsumableInputSchema = z
  .object({
    customValues: z.record(z.string(), z.unknown()).default({}),
    modelId: z.string().uuid(),
    name: shortText,
    unit: z.string().trim().min(1).max(40),
    precision: z.number().int().min(0).max(6).default(0),
    notes,
    visibility: contentVisibilitySchema.optional(),
  })
  .strict()
export const inventoryBatchInputSchema = z
  .object({
    consumableId: z.string().uuid(),
    supplierId: nullableId,
    purchaseId: nullableId,
    resourceId: nullableId,
    lotReference: z.string().trim().max(160).nullable().optional(),
    initialQuantity: z.number().positive().max(1_000_000_000),
    expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
  })
  .strict()
export const stockMovementInputSchema = z
  .object({
    batchId: z.string().uuid(),
    type: z.enum(['in', 'out', 'adjustment']),
    quantity: z.number().positive().max(1_000_000_000),
    resourceId: nullableId,
    reason: z.string().trim().min(1).max(1000),
  })
  .strict()
export const stockTransferInputSchema = z
  .object({
    batchId: z.string().uuid(),
    destinationResourceId: z.string().uuid(),
    quantity: z.number().positive().max(1_000_000_000),
    reason: z.string().trim().min(1).max(1000),
  })
  .strict()
export const inventoryConsumptionInputSchema = z
  .object({
    quantity: z.number().positive().max(1_000_000_000),
    reason: z.string().trim().min(1).max(1000),
  })
  .strict()
export const inventoryStockSetInputSchema = z
  .object({
    quantity: z.number().nonnegative().max(1_000_000_000),
    resourceId: nullableId,
    reason: z.string().trim().min(1).max(1000),
  })
  .strict()
export const inventoryBatchUpdateInputSchema = z
  .object({
    consumableId: z.string().uuid(),
    supplierId: nullableId,
    purchaseId: nullableId,
    resourceId: nullableId,
    lotReference: z.string().trim().max(160).nullable().optional(),
    expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
  })
  .strict()
export const inventoryLicensePoolInputSchema = z
  .object({
    customValues: z.record(z.string(), z.unknown()).default({}),
    modelId: z.string().uuid(),
    supplierId: nullableId,
    statusId: z.string().uuid(),
    contractReference: z.string().trim().max(160).nullable().optional(),
    seats: z.number().int().positive().max(1_000_000),
    startsAt: z.iso.datetime({ offset: true }).nullable().optional(),
    expiresAt: z.iso.datetime({ offset: true }).nullable().optional(),
    renewal: z.enum(['manual', 'automatic']),
    cost: moneySchema.nullable().optional(),
    billingPeriod: z.enum(['once', 'monthly', 'quarterly', 'yearly']).nullable().optional(),
    secret: z.string().max(100_000).nullable().optional(),
    notes,
    visibility: contentVisibilitySchema.optional(),
  })
  .strict()
export const inventoryLicenseAssignmentSchema = z
  .object({
    target: inventoryTargetSchema,
    comment: z.string().trim().max(4000).nullable().optional(),
  })
  .strict()
export const inventoryKitInputSchema = z
  .object({
    name: shortText,
    description: z.string().trim().max(2000).nullable().optional(),
    assetIds: z
      .array(z.string().uuid())
      .min(1)
      .max(250)
      .refine((ids) => new Set(ids).size === ids.length, 'Éléments dupliqués'),
    visibility: contentVisibilitySchema.optional(),
  })
  .strict()
export const inventoryPurchaseInputSchema = z
  .object({
    supplierId: nullableId,
    purchasedAt: z.iso.datetime({ offset: true }),
    orderReference: z.string().trim().max(160).nullable().optional(),
    invoiceReference: z.string().trim().max(160).nullable().optional(),
    cost: moneySchema,
    warrantyEndsAt: z.iso.datetime({ offset: true }).nullable().optional(),
    depreciatesAt: z.iso.datetime({ offset: true }).nullable().optional(),
    notes,
    final: z.boolean().default(false),
  })
  .strict()
export const inventorySettingsSchema = z
  .object({
    assetTagPrefix: z
      .string()
      .trim()
      .regex(/^[A-Z0-9_-]{1,16}$/)
      .default('AST-'),
    assetTagPadding: z.number().int().min(1).max(12).default(6),
    invoicePolicy: z.enum(['disabled', 'optional', 'required']).default('optional'),
    licenseAlertDays: z.array(z.number().int().min(0).max(3650)).min(1).max(10).default([30, 7, 0]),
    warrantyAlertDays: z
      .array(z.number().int().min(0).max(3650))
      .min(1)
      .max(10)
      .default([30, 7, 0]),
    documentMaxBytes: z.number().int().min(1024).max(100_000_000).default(10_000_000),
  })
  .strict()

export type InventoryAssetInput = z.infer<typeof inventoryAssetInputSchema>
export type InventoryModelInput = z.infer<typeof inventoryModelInputSchema>
export type InventoryCheckoutInput = z.infer<typeof inventoryCheckoutSchema>
export type InventoryCheckinInput = z.infer<typeof inventoryCheckinSchema>
export type InventorySettings = z.infer<typeof inventorySettingsSchema>
